import React, { useState, useRef } from 'react';
import { Upload, Button, Progress, message, Modal } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { videoUploadService } from '../../services/knowledgeBase/videoUploadService';
import type { VideoInfo } from '../../services/knowledgeBase/knowledgeBaseService';

interface VideoUploaderProps {
  value?: VideoInfo;
  onChange?: (video: VideoInfo | null) => void;
  maxSize?: number; // 最大文件大小（MB），默认500MB
  chunkSize?: number; // 分片大小（MB），默认10MB
}

const VideoUploader: React.FC<VideoUploaderProps> = ({
  value,
  onChange,
  maxSize = 500,
  chunkSize = 10,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const uploadIdRef = useRef<string | null>(null);

  // 分片上传
  const uploadMultipart = async (file: File) => {
    const CHUNK_SIZE = chunkSize * 1024 * 1024; // 转换为字节
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    
    try {
      // 初始化分片上传
      const initResult = await videoUploadService.initMultipartUpload(
        file.name,
        file.size
      );

      uploadIdRef.current = initResult.uploadId;

      // 上传每个分片
      for (let i = 1; i <= totalChunks; i++) {
        if (abortControllerRef.current?.signal.aborted) {
          // 取消上传
          if (uploadIdRef.current) {
            await videoUploadService.abortMultipartUpload(uploadIdRef.current);
          }
          throw new Error('上传已取消');
        }

        const start = (i - 1) * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        // 上传分片
        await videoUploadService.uploadChunk(
          initResult.uploadId,
          i,
          totalChunks,
          file.name,
          chunk
        );

        // 更新进度
        const progress = Math.round((i / totalChunks) * 100);
        setUploadProgress(progress);
        setUploadStatus(`正在上传分片 ${i}/${totalChunks}...`);
      }

      // 完成分片上传
      const result = await videoUploadService.completeMultipartUpload(
        initResult.uploadId,
        file.name,
        totalChunks
      );

      return result;
    } catch (error: any) {
      // 如果出错，尝试取消上传
      if (uploadIdRef.current && !abortControllerRef.current?.signal.aborted) {
        try {
          await videoUploadService.abortMultipartUpload(uploadIdRef.current);
        } catch (e) {
          console.error('取消上传失败:', e);
        }
      }
      throw error;
    }
  };

  // 直接上传（小文件）
  const uploadDirect = async (file: File) => {
    const xhr = new XMLHttpRequest();

    return new Promise<VideoInfo>((resolve, reject) => {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(progress);
          setUploadStatus('正在上传...');
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success) {
              resolve(response.data);
            } else {
              reject(new Error(response.message || '上传失败'));
            }
          } catch (error) {
            reject(new Error('解析响应失败'));
          }
        } else {
          reject(new Error('上传失败'));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('上传失败'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('上传已取消'));
      });

      // 获取token
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('video', file);

      xhr.open('POST', '/api/video-upload');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.send(formData);

      // 保存xhr引用以便取消
      (abortControllerRef.current as any).xhr = xhr;
    });
  };

  // 处理文件上传
  const handleUpload = async (file: File) => {
    // 验证文件大小
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSize) {
      message.error(`文件大小不能超过 ${maxSize}MB`);
      return false;
    }

    // 验证文件类型
    const allowedTypes = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedTypes.includes(ext)) {
      message.error(`不支持的视频格式，支持的格式：${allowedTypes.join(', ')}`);
      return false;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('准备上传...');
    abortControllerRef.current = new AbortController();
    uploadIdRef.current = null;

    try {
      let result: VideoInfo;

      // 如果文件大于100MB，使用分片上传
      if (file.size > 100 * 1024 * 1024) {
        setUploadStatus('使用分片上传...');
        result = await uploadMultipart(file);
      } else {
        result = await uploadDirect(file);
      }

      // 创建视频信息对象
      const videoInfo: VideoInfo = {
        key: result.key,
        url: result.url,
        fileName: result.fileName || file.name,
        fileSize: result.fileSize || file.size,
        duration: result.duration || 0,
        resolution: result.resolution || { width: 0, height: 0 },
        thumbnail: result.thumbnail || '',
        status: 'ready',
        variants: [],
      };

      onChange?.(videoInfo);
      setUploadProgress(100);
      setUploadStatus('上传成功');
      message.success('视频上传成功');
      
      return false; // 阻止默认上传行为
    } catch (error: any) {
      console.error('上传失败:', error);
      message.error(error.message || '上传失败，请重试');
      setUploadStatus('上传失败');
      return false;
    } finally {
      setUploading(false);
      setTimeout(() => {
        setUploadProgress(0);
        setUploadStatus('');
      }, 2000);
    }
  };

  // 删除视频
  const handleDelete = () => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个视频吗？',
      onOk: () => {
        onChange?.(null);
        message.success('视频已删除');
      },
    });
  };

  // 取消上传
  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      // 如果有xhr，也取消xhr
      if ((abortControllerRef.current as any).xhr) {
        (abortControllerRef.current as any).xhr.abort();
      }
      // 如果有分片上传，取消分片上传
      if (uploadIdRef.current) {
        videoUploadService.abortMultipartUpload(uploadIdRef.current).catch(console.error);
      }
    }
    setUploading(false);
    setUploadProgress(0);
    setUploadStatus('');
    message.info('上传已取消');
  };

  return (
    <div>
      {value && value.key ? (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>已上传视频: {value.fileName}</span>
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
              disabled={uploading}
            >
              删除
            </Button>
          </div>
          {value.url && (
            <video
              src={value.url}
              controls
              style={{ width: '100%', maxWidth: 600, marginTop: 8 }}
            >
              您的浏览器不支持视频播放
            </video>
          )}
        </div>
      ) : null}

      {uploading && (
        <div style={{ marginTop: 16 }}>
          <Progress percent={uploadProgress} status="active" />
          <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
            {uploadStatus}
          </div>
          <Button
            type="link"
            danger
            onClick={handleCancel}
            style={{ marginTop: 8 }}
          >
            取消上传
          </Button>
        </div>
      )}

      {!uploading && (
        <Upload
          beforeUpload={(file) => {
            handleUpload(file);
            return false; // 阻止默认上传
          }}
          accept="video/*"
          showUploadList={false}
          disabled={uploading}
        >
          <Button icon={<UploadOutlined />} disabled={uploading}>
            {value && value.key ? '重新上传视频' : '上传视频'}
          </Button>
        </Upload>
      )}

      <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
        支持格式：MP4, AVI, MOV, WMV, FLV, WEBM, MKV | 最大大小：{maxSize}MB | 大文件（&gt;100MB）将使用分片上传
      </div>
    </div>
  );
};

export default VideoUploader;
